'use client';

import { Button } from '@shared/components/shadcn';
import { Login } from '@shared/icons';
import { NumberFormats, ROUTES, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { LoginButton } from '../../LoginButton/index';
import { useLatestTransactionSummary } from '../../my-account/Wallet/hooks/useLatestTransactionSummary';
import { useWalletBalance } from '../../my-account/Wallet/hooks/useWalletBalance';
import { useWalletBalanceHold } from '../../my-account/Wallet/hooks/useWalletBalanceHold';
import { WalletPopover } from '../../wallet/WalletPopover/WalletPopover';
import { AvatarButton } from './AvatarButton/AvatarButton';
import { HamburgerMenu, type HamburgerMenuItemProps } from './HamburgerMenu/HamburgerMenu';
import type { NotificationSeverity } from './NotificationDot/NotificationDot';

type LoggedInActionsProps = {
  balance: string;
  avatarLabel: string;
  notificationSeverity?: NotificationSeverity;
};

const { REGISTER } = ROUTES;

function LoggedInActions({ balance, avatarLabel, notificationSeverity }: LoggedInActionsProps) {
  return (
    <>
      <WalletPopover balance={balance} />
      <AvatarButton label={avatarLabel} href={'/my-account'} notificationSeverity={notificationSeverity} />
    </>
  );
}

function LoggedOutActions({ namespace }: { namespace: string }) {
  const t = useTranslations(namespace);
  return (
    <>
      <Button variant="primary" size="s" asChild>
        <Link href={REGISTER} data-track-name="Register">
          {t('join')}
        </Link>
      </Button>
      <LoginButton className="hidden sm:flex gap-2" variant="ghost" size="s" data-track-name="Log In" data-testid="desktop-login-button">
        {t('login')}
        <Login size="extra-small" />
      </LoginButton>
      <LoginButton className="flex sm:hidden" variant="ghost" size="icon-m" aria-label={t('login')} data-track-name="Log In" data-testid="mobile-login-button">
        <Login size="medium" />
      </LoginButton>
    </>
  );
}

type ActionsBarProps = {
  avatarLabel: string;
  notificationSeverity?: NotificationSeverity;
  hasNotifications: boolean;
  searchSlot?: React.ReactNode;
  playerId?: string;
  topMenuItems?: Omit<HamburgerMenuItemProps, 'onMenuItemClick'>[];
  bottomMenuItems?: Omit<HamburgerMenuItemProps, 'onMenuItemClick'>[];
};

function ActionsBar({ avatarLabel, notificationSeverity, hasNotifications, searchSlot, playerId, topMenuItems, bottomMenuItems }: ActionsBarProps) {
  const formatCurrency = useCurrencyFormatter();

  const { accountSummary, isLoading: isWalletTransactionsLoading } = useLatestTransactionSummary();
  const { totalBalance, isLoading: isWalletBalanceLoading } = useWalletBalance();

  const { availableBalance } = accountSummary || {};
  const { balance: balanceToShow } = useWalletBalanceHold(totalBalance ?? availableBalance);
  const isBalanceLoading = isWalletTransactionsLoading || (isWalletBalanceLoading && !accountSummary);
  const formattedBalance =
    balanceToShow !== undefined
      ? formatCurrency(balanceToShow).format(NumberFormats.WITH_DECIMALS).get()
      : isBalanceLoading
        ? '...'
        : formatCurrency(0).format(NumberFormats.WITH_DECIMALS).get();

  return (
    <div className="flex flex-row items-center gap-2" data-track-container="Actions">
      {searchSlot}
      {playerId ? (
        <LoggedInActions balance={formattedBalance} avatarLabel={avatarLabel} notificationSeverity={notificationSeverity} />
      ) : (
        <LoggedOutActions namespace={'header.actions_bar'} />
      )}
      {/* <CartButton itemsAddedToCart={3} /> */}
      <HamburgerMenu topMenuItems={topMenuItems} bottomMenuItems={bottomMenuItems} namespace={'header.hamburger_menu'} hasNotifications={hasNotifications} />
    </div>
  );
}

export { ActionsBar };
